import { useEffect, useState } from 'react';
import { Button, Modal, type OverlayTriggerState } from '@heroui/react';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { AsyncButton } from '../../../components/ui/async-button';
import { DrawerTextAreaField } from '../../../components/ui/drawer-form-field';
import type { RecentMisuseReport } from '../../../api/misuse-reports';

interface AlreadyResolvedDialogProps {
  state: OverlayTriggerState;
  question: string;
  onConfirm: () => Promise<void>;
}

export function AlreadyResolvedMisuseDialog({
  state,
  question,
  onConfirm,
}: AlreadyResolvedDialogProps) {
  return (
    <ConfirmDialog
      state={state}
      title="Já resolvido"
      message={<p className="text-muted">{question}</p>}
      cancelLabel="Não"
      confirmLabel="Sim"
      onConfirm={onConfirm}
    />
  );
}

interface WillRemoveDialogProps {
  state: OverlayTriggerState;
  report: RecentMisuseReport;
  onConfirm: (reason: string) => Promise<void>;
}

export function WillRemoveMisuseDialog({ state, report, onConfirm }: WillRemoveDialogProps) {
  const [step, setStep] = useState<'reason' | 'confirm'>('reason');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!state.isOpen) return;
    setStep('reason');
    setReason('');
    setSubmitting(false);
  }, [state.isOpen]);

  const handleClose = () => {
    if (submitting) return;
    state.close();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      state.close();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal state={state}>
      <Modal.Backdrop isDismissable={!submitting}>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>
                {step === 'reason' ? 'OK, vou retirar' : 'Confirmar retirada'}
              </Modal.Heading>
              <Modal.CloseTrigger />
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-3">
              {step === 'reason' ? (
                <>
                  <p className="text-muted">
                    Houve algum motivo para haver estacionado no lugar errado?
                  </p>
                  <DrawerTextAreaField
                    label="Motivo"
                    value={reason}
                    onChange={setReason}
                    placeholder="Opcional"
                    rows={3}
                    isDisabled={submitting}
                  />
                </>
              ) : (
                <p className="text-muted">
                  Confirma que você vai retirar o carro da vaga {report.spotCode}
                  {report.licensePlate ? ` (placa ${report.licensePlate})` : ''}?
                </p>
              )}
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-2">
              {step === 'reason' ? (
                <>
                  <Button variant="secondary" isDisabled={submitting} onPress={handleClose}>
                    Cancelar
                  </Button>
                  <Button variant="primary" isDisabled={submitting} onPress={() => setStep('confirm')}>
                    Continuar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    isDisabled={submitting}
                    onPress={() => setStep('reason')}
                  >
                    Voltar
                  </Button>
                  <AsyncButton
                    variant="primary"
                    isPending={submitting}
                    pendingLabel="Encerrando"
                    onPress={handleConfirm}
                  >
                    Sim, vou retirar
                  </AsyncButton>
                </>
              )}
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
